import type { ConversationSlaSnapshot } from '@troia-v3/types';

import { minutesSince } from './time';

/**
 * `ok` = relógio CORRENDO e dentro do prazo — verde, "estamos no prazo"
 * (decisão do dono, 16/08: prazo em dia não pode ser cinza).
 * `neutral` = a dívida NÃO é nossa agora: bola com o cliente (pausado), ou
 * relógio encerrado/ausente. Cinza de propósito — verde diria "estamos bem"
 * quando não há relógio nos cobrando, e vermelho cobraria o atendente pelo
 * silêncio do cliente.
 */
export type CooldownLevel = 'ok' | 'neutral' | 'warning' | 'critical';

export interface CooldownThresholds {
  /** Até aqui a conversa está "quente" — sem sinal. */
  neutralUpToMinutes: number;
  /** Daqui até aqui, atenção (âmbar). Acima, crítico (vermelho). */
  warningUpToMinutes: number;
  /** Onde o trilho fica cheio. Não é limiar de cor, é escala da barra. */
  fullAtMinutes: number;
}

/**
 * FALLBACK da barra de esfriamento: neutro até 5 minutos, âmbar até 30,
 * vermelho acima (decisão de 27/07/2026).
 *
 * Deixou de ser o caminho principal no Plano D. Quando o card recebe
 * `sla` (`ConversationSlaSnapshot`, medido pelo SERVIDOR em tempo útil), é
 * ele que governa cor e trilho. Estes números só valem quando o snapshot está
 * AUSENTE: conversa que nunca recebeu inbound, ou empresa sem política de
 * conversa. Relógio PARADO (`met`/`cancelled`) NÃO cai aqui — o snapshot
 * continua vindo e o ramo do SLA o trata como `neutral`.
 *
 * ⚠️ NUNCA acrescente aqui uma leitura de horário comercial. O frontend não
 * tem — e não deve ter — o calendário da empresa, o fuso nem os feriados.
 * Medir tempo útil no cliente é como este arquivo pintava de vermelho toda
 * a sexta 18:01 até segunda 08:00, para um alvo que não consumiu um minuto.
 *
 * `fullAtMinutes` é a escala do trilho (cheio a partir de 24h), não um
 * limiar de cor.
 */
export const CONVERSATION_COOLDOWN_THRESHOLDS: CooldownThresholds = {
  neutralUpToMinutes: 5,
  warningUpToMinutes: 30,
  fullAtMinutes: 24 * 60,
};

export interface CooldownState {
  level: CooldownLevel;
  /** Fração do trilho preenchida, 0..1. */
  ratio: number;
  /** Minutos desde a última mensagem (0 quando não há data). */
  minutes: number;
  /** Quem governou a decisão. `'sla'` = veio do servidor, em tempo útil. */
  source: 'sla' | 'fallback';
}

export interface ResolveCooldownOptions {
  /**
   * A vez é nossa (o cliente falou por último). SÓ nesse caso a barra AQUECE —
   * é o que distingue "o cliente está esperando" de "nós estamos esperando".
   * Com a vez do cliente, o trilho continua enchendo (mede o silêncio), mas em
   * tom neutro: o tempo parado não é dívida nossa.
   */
  waitingOnUs: boolean;
  /**
   * Leitura do relógio de primeira resposta, JÁ medida pelo servidor em
   * tempo útil. Quando presente, GOVERNA: o tempo de parede e as faixas fixas
   * são ignorados por completo.
   *
   * ⚠️ NÃO existe prop de `thresholds` aqui, de propósito (revisão final do
   * Plano D): os limiares REAIS são os da política, e eles chegam DENTRO do
   * snapshot (`targetMs`/`warnAtMs`), já medidos. Um segundo canal de limiares
   * no cliente convidaria a mesma tela a pintar por dois critérios diferentes.
   */
  sla?: ConversationSlaSnapshot;
  now?: Date;
}

/**
 * As três faixas ocupam um terço do trilho cada, com rampa linear dentro da
 * faixa. Assim o trilho é legível de longe: menos de 1/3 é conversa viva, mais
 * de 2/3 é conversa morrendo, independente do valor absoluto.
 */
export const resolveConversationCooldown = (
  lastMessageAt: string | undefined,
  options: ResolveCooldownOptions,
): CooldownState => {
  const minutes = minutesSince(lastMessageAt, options.now) ?? 0;

  if (options.sla) {
    const { state, elapsedBusinessMs, warnAtMs, targetMs } = options.sla;
    // Trilho = consumo do alvo (não mais 24h de parede): cheio no
    // vencimento. É a única escala que corresponde ao que o número ao lado
    // significa.
    const ratio = targetMs > 0 ? Math.min(1, Math.max(0, elapsedBusinessMs / targetMs)) : 0;

    // Pausado / parado: a dívida não é nossa agora. `paused` num relógio de
    // conversa significa literalmente "nós já respondemos" — pintar de
    // vermelho seria cobrar o operador pelo silêncio do cliente.
    if (state === 'paused' || state === 'met' || state === 'cancelled') {
      return { level: 'neutral', ratio, minutes, source: 'sla' };
    }
    if (state === 'breached' || elapsedBusinessMs >= targetMs) {
      return { level: 'critical', ratio: 1, minutes, source: 'sla' };
    }
    if (elapsedBusinessMs >= warnAtMs) {
      return { level: 'warning', ratio, minutes, source: 'sla' };
    }
    // Correndo e dentro do prazo: VERDE — a única faixa em que "tudo certo"
    // é uma afirmação verdadeira sobre um relógio que está nos cobrando.
    return { level: 'ok', ratio, minutes, source: 'sla' };
  }

  // ---- Fallback: tempo de PAREDE, faixas fixas. Comportamento anterior,
  //      preservado byte a byte para o card que ainda não recebe snapshot.
  const t = CONVERSATION_COOLDOWN_THRESHOLDS;
  if (minutesSince(lastMessageAt, options.now) === null) {
    return { level: 'neutral', ratio: 0, minutes: 0, source: 'fallback' };
  }

  const third = 1 / 3;
  const band = (from: number, to: number, floor: number): number =>
    floor + ((minutes - from) / (to - from)) * third;

  if (minutes < t.neutralUpToMinutes) {
    return {
      // Fallback (sem snapshot): com a vez NOSSA e dentro da 1ª faixa, verde;
      // com a bola no cliente, cinza — mesma semântica do ramo do SLA.
      level: options.waitingOnUs ? 'ok' : 'neutral',
      ratio: band(0, t.neutralUpToMinutes, 0),
      minutes,
      source: 'fallback',
    };
  }
  if (minutes < t.warningUpToMinutes) {
    return {
      level: options.waitingOnUs ? 'warning' : 'neutral',
      ratio: band(t.neutralUpToMinutes, t.warningUpToMinutes, third),
      minutes,
      source: 'fallback',
    };
  }
  return {
    level: options.waitingOnUs ? 'critical' : 'neutral',
    ratio: Math.min(1, band(t.warningUpToMinutes, t.fullAtMinutes, third * 2)),
    minutes,
    source: 'fallback',
  };
};

/** Cores do nível — tokens, nunca hex. `solid` pinta a barra; `fg` pinta o número. */
export const COOLDOWN_COLORS: Record<CooldownLevel, { solid: string; fg: string }> = {
  ok: { solid: 'var(--theme-success-color)', fg: 'var(--theme-success-fg)' },
  neutral: { solid: 'var(--theme-muted-text)', fg: 'var(--theme-muted-text)' },
  warning: { solid: 'var(--theme-warning-color)', fg: 'var(--theme-warning-fg)' },
  critical: { solid: 'var(--theme-error-color)', fg: 'var(--theme-error-fg)' },
};
